/**
 * Module-level bridge for "open a Deckorate panel" requests coming from chat.
 *
 * A chat pill can't navigate on its own: the pill handler runs outside React
 * (a static map in handlers.ts), and the panel it needs to open lives in a
 * DIFFERENT top-level tab from the chat pane. Opening it takes two React
 * components acting together:
 *
 *   1. TaskPane switches the active tab to "deckorate" (mounting the module).
 *   2. DeckorateModule sets its own `view` to the requested panel.
 *
 * Hence a listener Set rather than the single-sink shape used by
 * pill-bridge/toast-bridge — this bridge genuinely has two subscribers.
 *
 * The request is ALSO parked in `pending` because those two steps are not
 * simultaneous: when chat is the active tab, DeckorateModule isn't mounted
 * yet and so has no listener registered when the request fires. It reads the
 * parked target on mount instead (see consumePendingDeckorateView).
 */

/** Deckorate panels reachable from chat. Values must stay assignable to
 *  DeckorateModule's `View` union — that's what receives them. */
export type DeckorateNavTarget =
  | "apply-brand"
  | "brand-check"
  | "map"
  | "org-chart"
  | "infographic"
  | "speaker-notes";

/** `null` means "bring the Deckorate tab forward, leave its panel alone" —
 *  see requestDeckorateTab. */
type NavListener = (target: DeckorateNavTarget | null) => void;

const listeners = new Set<NavListener>();

/** Set by requestDeckorateView, cleared by whoever applies it. Only the most
 *  recent request survives — a second click before the module mounts should
 *  land on the newer target, not queue both. */
let pending: DeckorateNavTarget | null = null;

/** Subscribe to navigation requests. Returns an unsubscribe for effect cleanup. */
export function subscribeDeckorateNav(listener: NavListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Ask the add-in to open a Deckorate panel. Safe to call from outside React. */
export function requestDeckorateView(target: DeckorateNavTarget): void {
  pending = target;
  for (const listener of listeners) listener(target);
}

/**
 * Ask the add-in to bring the Deckorate tab forward without changing which
 * panel is showing — used by Code Mode's leader, which needs DeckorateModule
 * mounted (that is where the keydown listener lives) but has no panel in mind
 * until the user types a code.
 *
 * Nothing is parked: there is no view to apply later, so a module mounting
 * afterwards has nothing to claim.
 */
export function requestDeckorateTab(): void {
  for (const listener of listeners) listener(null);
}

/**
 * Read and clear the parked target.
 *
 * Called by DeckorateModule on mount (to pick up a request made while it was
 * unmounted) and from its listener (to discard the park when it handled the
 * request live). Clearing in both paths is what stops a stale target from
 * hijacking a later, unrelated open of the module.
 */
export function consumePendingDeckorateView(): DeckorateNavTarget | null {
  const target = pending;
  pending = null;
  return target;
}
